import { TextField, TextFieldProps } from '@mui/material'
import { ChangeEvent, forwardRef } from 'react'
import { formatWithSpacesInline } from './_utils/utils'

export type StyledTextFieldProps = {
  className?: string
  formatNumber?: boolean
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  darkTheme?: boolean
} & TextFieldProps

export const StyledTextField = forwardRef<HTMLDivElement, StyledTextFieldProps>(
  (
    {
      className = '',
      formatNumber,
      onChange,
      darkTheme,
      error,
      multiline,
      ...rest
    },
    ref
  ) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const formattedValue = formatNumber
        ? formatWithSpacesInline(inputValue)
        : inputValue

      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: formattedValue,
        },
      }

      onChange?.(syntheticEvent as ChangeEvent<HTMLInputElement>)
    }

    return (
      <TextField
        ref={ref}
        className={className}
        multiline={multiline}
        sx={(theme) => ({
          width: '100%',
          minHeight: '56px',
          '& input:-webkit-autofill': {
            borderRadius: 1,
          },
          '& input:autofill': {
            borderRadius: 1,
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
            backgroundColor: darkTheme
              ? theme.palette.custom.zBlack300
              : theme.palette.custom.zGrey200,
            border: 'none',
            outline: error
              ? `2px solid ${theme.palette.custom.zRedError}`
              : 'none',
            outlineOffset: '0',
            // Conditional padding - remove padding for multiline, keep for single line
            padding: multiline ? '0' : '15px 16px',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
            '&.Mui-focused': {
              outline: `1px solid ${theme.palette.custom.zBlack200}`,
              outlineOffset: 0,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          // Single line input styles
          '& .MuiOutlinedInput-input': {
            padding: '0',
            fontSize: '16px',
            lineHeight: '26px',
            fontWeight: 500,
            color: `${
              darkTheme
                ? theme.palette.custom.zWhite100
                : theme.palette.custom.zBlack200
            } !important`,
            '&::placeholder': {
              color: theme.palette.custom.zGrey400,
              opacity: 1,
            },
          },
          // Multiline input styles - apply padding here instead of root
          '& .MuiInputBase-inputMultiline': {
            padding: '16px !important',
            fontSize: '16px',
            lineHeight: '26px',
            fontWeight: 500,
            color: `${
              darkTheme
                ? theme.palette.custom.zWhite100
                : theme.palette.custom.zBlack200
            } !important`,
            '&::placeholder': {
              color: theme.palette.custom.zGrey400,
              opacity: 1,
            },
          },
        })}
        onChange={handleChange}
        error={error}
        {...rest}
      />
    )
  }
)

StyledTextField.displayName = 'StyledTextField'
